import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as TransferIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Business {
  business_id: string;
  business_name: string;
  business_email: string;
  phone_number: string;
  address: string;
  logo_url?: string;
  subscription_status: string;
  subscription_tier: string;
  created_at: string;
  user_count: number;
  location_count: number;
  access_granted_at: string;
}

interface AssetSummary {
  asset_id: string;
  asset_name: string;
  asset_type: string;
  serial_number: string;
  status: string;
  updated_at: string;
}

type SectionKey = 'overview' | 'transfer';

const MultiBusinessDashboard: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [switching, setSwitching] = useState(false);
  const [selectedSection, setSelectedSection] = useState<SectionKey>(
    (searchParams.get('section') === 'transfer' ? 'transfer' : 'overview') as SectionKey
  );

  const [transferFrom, setTransferFrom] = useState<string>('');
  const [transferTo, setTransferTo] = useState<string>('');
  const [availableAssets, setAvailableAssets] = useState<AssetSummary[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [businessSearch, setBusinessSearch] = useState('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [transferring, setTransferring] = useState(false);
  const [transferFeedback, setTransferFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'transfer') {
      setSelectedSection('transfer');
    } else {
      setSelectedSection('overview');
    }
  }, [searchParams]);

  useEffect(() => {
    if (transferFrom && transferTo && transferFrom === transferTo) {
      setTransferTo('');
    }
  }, [transferFrom, transferTo]);

  useEffect(() => {
    if (transferFrom) {
      fetchAssetsForBusiness(transferFrom);
      setSelectedAssetIds([]);
    } else {
      setAvailableAssets([]);
      setSelectedAssetIds([]);
    }
  }, [transferFrom]);


  const fetchBusinesses = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/multi-business-manager/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const sorted = [...(response.data.businesses || [])].sort((a: Business, b: Business) =>
          (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
        );
        setBusinesses(sorted);
        setLastUpdated(new Date().toISOString());
      } else {
        setError('Failed to load businesses');
      }
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
      setError(err.response?.data?.error || 'Failed to load businesses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAssetsForBusiness = async (businessId: string) => {
    try {
      setLoadingAssets(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/multi-business-manager/assets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { businessId }
      });

      if (response.data.success) {
        setAvailableAssets(response.data.assets || []);
      } else {
        setTransferFeedback({
          type: 'error',
          message: response.data.error || 'Failed to load assets for this business.'
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch assets:', err);
      setTransferFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Failed to load assets for this business.'
      });
    } finally {
      setLoadingAssets(false);
    }
  };

  const filteredAssets = useMemo(() => {
    const query = assetSearch.toLowerCase();
    if (!query) return availableAssets;
    return availableAssets.filter(asset =>
      asset.asset_name.toLowerCase().includes(query) ||
      asset.asset_type.toLowerCase().includes(query) ||
      (asset.serial_number || '').toLowerCase().includes(query)
    );
  }, [availableAssets, assetSearch]);

  const filteredBusinesses = useMemo(() => {
    const query = businessSearch.trim().toLowerCase();
    const list = !query
      ? businesses
      : businesses.filter(b =>
          (b.business_name || '').toLowerCase().includes(query) ||
          (b.address || '').toLowerCase().includes(query) ||
          (b.business_email || '').toLowerCase().includes(query)
        );
    return [...list].sort((a, b) =>
      (a.business_name || '').localeCompare(b.business_name || '', undefined, { sensitivity: 'base' })
    );
  }, [businesses, businessSearch]);

  const handleSelectBusiness = (business: Business) => {
    setSelectedBusiness(business);
    setConfirmDialogOpen(true);
  };

  const handleConfirmSwitch = async () => {
    if (!selectedBusiness) return;

    try {
      setSwitching(true);
      const token = localStorage.getItem('token');
      localStorage.setItem('multiBusinessManagerToken', token || '');

      const response = await axios.post(
        `${API_BASE_URL}/multi-business-manager/switch-business`,
        { businessId: selectedBusiness.business_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('inBusinessContext', 'true');
        localStorage.setItem('businessName', selectedBusiness.business_name);
        localStorage.setItem('businessId', selectedBusiness.business_id);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        window.location.href = '/admin/dashboard';
      }
    } catch (err: any) {
      console.error('Error switching business:', err);
      setError(err.response?.data?.error || 'Failed to switch business');
    } finally {
      setSwitching(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleCancelSwitch = () => {
    setConfirmDialogOpen(false);
    setSelectedBusiness(null);
  };

  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssetIds(prev =>
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAssetIds(filteredAssets.map(asset => asset.asset_id));
    } else {
      setSelectedAssetIds([]);
    }
  };

  const handleTransferAssets = async () => {
    if (!transferFrom || !transferTo) {
      setTransferFeedback({
        type: 'error',
        message: 'Please select both a source and destination business.'
      });
      return;
    }

    if (transferFrom === transferTo) {
      setTransferFeedback({
        type: 'error',
        message: 'Source and destination must be different businesses.'
      });
      return;
    }

    if (selectedAssetIds.length === 0) {
      setTransferFeedback({
        type: 'error',
        message: 'Select at least one asset to transfer.'
      });
      return;
    }

    try {
      setTransferring(true);
      setTransferFeedback(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/multi-business-manager/transfer-assets`,
        {
          assetIds: selectedAssetIds,
          fromBusinessId: transferFrom,
          toBusinessId: transferTo
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTransferFeedback({
          type: 'success',
          message: `Successfully transferred ${response.data.transferred || selectedAssetIds.length} assets.`
        });
        setSelectedAssetIds([]);
        await fetchAssetsForBusiness(transferFrom);
      } else {
        setTransferFeedback({
          type: 'error',
          message: response.data.error || 'Failed to transfer assets.'
        });
      }
    } catch (err: any) {
      console.error('Failed to transfer assets:', err);
      setTransferFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Failed to transfer assets.'
      });
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}> 
        <CircularProgress />
      </Box>
    );
  }

  const activeBusinesses = businesses.filter(b => b.subscription_status === 'Active' || b.subscription_status === 'active').length;
  const complianceRate = businesses.length > 0 
    ? Math.round((activeBusinesses / businesses.length) * 100)
    : 0;

  const metricCards = [
    {
      key: 'businesses',
      label: 'Businesses',
      value: businesses.length.toLocaleString(),
      helper: 'Businesses you can access',
      color: theme.palette.primary.main,
      icon: <BusinessIcon fontSize="large" />,
    },
    {
      key: 'active',
      label: 'Active',
      value: activeBusinesses.toLocaleString(),
      helper: 'Businesses with active subscription',
      color: theme.palette.success.main,
      icon: <CheckCircleIcon fontSize="large" />,
    },
    {
      key: 'compliance',
      label: 'Compliance',
      value: `${complianceRate}%`,
      helper: 'Share of businesses active',
      color: theme.palette.warning.main,
      icon: <AssessmentIcon fontSize="large" />,
    },
  ];

  const cardSx = {
    borderRadius: 4,
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    '&:hover': { boxShadow: '0 20px 45px rgba(0,0,0,0.12)' },
  };

  const getMetricCardSx = (color: string) => ({
    p: 3,
    height: '100%',
    borderRadius: 4,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    bgcolor: alpha(color, 0.08),
    border: '1px solid',
    borderColor: alpha(color, 0.25),
    boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 32px ${alpha(color, 0.18)}`,
    },
  });

  return (
    <>
      {/* Dashboard Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4, 
          mb: 4,
          [theme.breakpoints.down('sm')]: {
            mt: 2,
            mb: 2,
            px: 2,
          },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Dashboard Snapshot - aligned with main admin dashboard */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Dashboard Snapshot
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Quick view of your businesses and access.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {lastUpdated && (
                <Typography variant="body2" color="text.secondary">
                  Updated {formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}
                </Typography>
              )}
              <Tooltip title="Refresh dashboard">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fetchBusinesses(true)}
                    disabled={refreshing || loading}
                    sx={{ color: 'text.secondary' }}
                  >
                    {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Grid container spacing={3}>
            {metricCards.map(card => (
              <Grid item xs={12} sm={6} md={4} key={card.key}>
                <Card sx={getMetricCardSx(card.color)}>
                  <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '16px',
                          backgroundColor: alpha(card.color, 0.2),
                          color: card.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary">{card.label}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {card.helper}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Section Toggle */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{ p: 3, ...cardSx }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedSection === 'overview' ? 'Business Management' : 'Asset Transfer'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant={selectedSection === 'overview' ? 'contained' : 'outlined'}
                    color="primary" 
                    onClick={() => {
                      setSelectedSection('overview');
                      setSearchParams({});
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Overview
                  </Button>
                  <Button 
                    variant={selectedSection === 'transfer' ? 'contained' : 'outlined'}
                    color="primary" 
                    onClick={() => {
                      setSelectedSection('transfer');
                      setSearchParams({ section: 'transfer' });
                    }}
                    startIcon={<TransferIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Transfer Assets
                  </Button>
                  <Tooltip title="Refresh businesses">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => fetchBusinesses(true)}
                        disabled={refreshing || loading}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Overview Section */}
        {selectedSection === 'overview' && (
          <Card sx={{ mb: 4, p: 3, ...cardSx }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>All Businesses</Typography>
              <TextField
                size="small"
                placeholder="Search businesses..."
                value={businessSearch}
                onChange={(e) => setBusinessSearch(e.target.value)}
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {businesses.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  You don't have any businesses assigned yet.
                </Typography>
              </Box>
            ) : filteredBusinesses.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No businesses match &quot;{businessSearch}&quot;. Try a different search.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                <Table>
                  <TableHead sx={{ bgcolor: 'background.default' }}>
                    <TableRow>
                      <TableCell>Business Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell>Locations</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBusinesses.map((business) => (
                      <TableRow key={business.business_id} hover>
                        <TableCell>{business.business_name}</TableCell>
                        <TableCell>{business.address || 'Not provided'}</TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block', 
                            px: 1.5, 
                            py: 0.5, 
                            borderRadius: 1,
                            bgcolor: (business.subscription_status === 'Active' || business.subscription_status === 'active') ? 'success.light' : 'warning.light',
                            color: (business.subscription_status === 'Active' || business.subscription_status === 'active') ? 'success.dark' : 'warning.dark',
                          }}>
                            {business.subscription_status || 'Active'}
                          </Box>
                        </TableCell>
                        <TableCell>{business.user_count || 0}</TableCell>
                        <TableCell>{business.location_count || 0}</TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="primary"
                            onClick={() => handleSelectBusiness(business)}
                            disabled={switching}
                            sx={{ borderRadius: 2 }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}

        {/* Transfer Section */}
        {selectedSection === 'transfer' && (
          <>
            {transferFeedback && (
              <Alert severity={transferFeedback.type} sx={{ mb: 2 }} onClose={() => setTransferFeedback(null)}>
                {transferFeedback.message}
              </Alert>
            )}

            <Card sx={{ mb: 4, p: 3, ...cardSx }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Transfer Assets Between Businesses
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Asset Type Handling:</strong> When assets are transferred, their asset types are automatically managed:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                  <li>If the destination business has a matching asset type (same name), the asset will use that type</li>
                  <li>If no match exists, a new asset type will be created in the destination business automatically</li>
                  <li>This ensures all transferred assets remain properly categorized and visible</li>
                </Typography>
              </Alert>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="transfer-from-label">Transfer from</InputLabel>
                    <Select
                      labelId="transfer-from-label"
                      value={transferFrom}
                      label="Transfer from"
                      onChange={event => setTransferFrom(event.target.value)}
                    >
                      {businesses.map(business => (
                        <MenuItem key={business.business_id} value={business.business_id}>
                          {business.business_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!transferFrom}>
                    <InputLabel id="transfer-to-label">Transfer to</InputLabel>
                    <Select
                      labelId="transfer-to-label"
                      value={transferTo}
                      label="Transfer to"
                      onChange={event => setTransferTo(event.target.value)}
                    >
                      {businesses
                        .filter(business => business.business_id !== transferFrom)
                        .map(business => (
                          <MenuItem key={business.business_id} value={business.business_id}>
                            {business.business_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                placeholder="Search assets by name, type, or serial number"
                sx={{ mb: 3 }}
                value={assetSearch}
                onChange={event => setAssetSearch(event.target.value)}
                disabled={!transferFrom || loadingAssets}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Checkbox
                  indeterminate={
                    selectedAssetIds.length > 0 && selectedAssetIds.length < filteredAssets.length
                  }
                  checked={
                    filteredAssets.length > 0 && selectedAssetIds.length === filteredAssets.length
                  }
                  onChange={(_, checked) => toggleSelectAll(checked)}
                  disabled={!transferFrom || filteredAssets.length === 0}
                />
                <Typography variant="body2" color="text.secondary">
                  Select all ({filteredAssets.length} assets{assetSearch ? ` matching "${assetSearch}"` : ''})
                </Typography>
              </Box>

              {loadingAssets ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !transferFrom ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Choose a source business to load its assets.
                  </Typography>
                </Box>
              ) : filteredAssets.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No assets match your current filters.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 3 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: 'background.default' }}>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Asset Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAssets.map(asset => {
                        const checked = selectedAssetIds.includes(asset.asset_id);
                        return (
                          <TableRow
                            key={asset.asset_id}
                            hover
                            selected={checked}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleAssetSelection(asset.asset_id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={checked}
                                onChange={() => toggleAssetSelection(asset.asset_id)}
                                onClick={event => event.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell>{asset.asset_name}</TableCell>
                            <TableCell>{asset.asset_type || 'Uncategorized'}</TableCell>
                            <TableCell>{asset.serial_number || '—'}</TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'inline-block', 
                                px: 1.5, 
                                py: 0.5, 
                                borderRadius: 1,
                                bgcolor: (asset.status?.toLowerCase() === 'active') ? 'success.light' : 'warning.light',
                                color: (asset.status?.toLowerCase() === 'active') ? 'success.dark' : 'warning.dark',
                              }}>
                                {asset.status || 'Unknown'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {asset.updated_at ? new Date(asset.updated_at).toLocaleString() : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box>
                  <Typography variant="subtitle2">
                    {selectedAssetIds.length} asset{selectedAssetIds.length === 1 ? '' : 's'} selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assets will be moved immediately and locations cleared.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<TransferIcon />}
                  disabled={
                    transferring ||
                    !transferFrom ||
                    !transferTo ||
                    transferFrom === transferTo ||
                    selectedAssetIds.length === 0
                  }
                  onClick={handleTransferAssets}
                  sx={{ borderRadius: 2 }}
                >
                  {transferring ? 'Transferring...' : 'Transfer Assets'}
                </Button>
              </Box>
            </Card>
          </>
        )}
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSwitch}
      >
        <DialogTitle>Switch to Business</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to switch to the <strong>{selectedBusiness?.business_name}</strong> workspace.
            This will give you access to all their data and features.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitch} disabled={switching} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSwitch} color="primary" variant="contained" disabled={switching}>
            {switching ? 'Switching...' : 'Switch Business'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MultiBusinessDashboard;
